import React from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../../../utils/animationVariants";
import styles from "./NaglowekPodstrony.module.css";

/**
 * NaglowekPodstrony — wspólny nagłówek podstrony (brief 2026-09-02, poz. 2/3/4/8).
 *
 * Właściciel: „ujednolicić UX dla każdej podstrony", „/obsluga-hr/grafiki JEST WYZNACZNIKIEM
 * UI APLIKACJI", „kafelki mają mieć wspólny rozmiar oraz responsywność". Dotąd każda z 89
 * podstron miała własny `.hero` w swoim module CSS — stąd rozjazd. Anatomia siedzi teraz
 * w jednym miejscu; strona podaje tylko treść.
 *
 * ── WZORZEC DOM (polecenie właściciela 2026-09-06, pkt 4) ────────────────────────────────
 * Nagłówek ma JEDEN sztywny kształt i nic poza nim:
 *
 *   div.hero
 *     div.heroGlow
 *     div.heroPattern
 *     div.heroContent
 *       div.heroLeft      → span.heroBadge (+ span.heroBadgeDot), h1.heroTitle, p.heroSubtitle
 *       div.heroStats     → div.heroStat  (span.heroStatIcon, span.heroStatValue, span.heroStatLabel)
 *
 * Co z niego wypadło i dlaczego — bo pomiar pokazał, że rozjeżdżał się WŁAŚNIE ten komponent,
 * a nie 86 stron z własnym nagłówkiem (te realizowały wzorzec już wcześniej):
 *   • `<header>` → `<div>`. Strona ma nagłówek jeden raz i wyznacza go `data-testid`,
 *     a nie znacznik HTML; `<header>` wewnątrz stron, które osadzają inne strony, dawał
 *     drugi punkt orientacyjny dla czytnika ekranu.
 *   • owijki `heroHeadline` / `heroHeadlineText` — h1 i p są teraz BEZPOŚREDNIMI dziećmi
 *     `heroLeft`, dokładnie tak jak na 86 stronach z własnym nagłówkiem.
 *   • `heroNote` — treść notek przeniesiona do `subtitle` na pięciu stronach, które ich
 *     używały (nic nie zginęło, zniknął tylko drugi akapit w nagłówku).
 *   • `tone` / `toneHr` — jeden odcień rodziny. Ta klasa i tak była widoczna wyłącznie przez
 *     pierwszą klatkę, zanim `styles/accent.css` nadpisał tło gradientem wybranej palety.
 *
 * ⚠ CZEGO NIE ZDJĘTO I DLACZEGO. Kafelek, w który da się kliknąć, zostaje `<button>`.
 * Pomiar: 28 takich kafelków na 8 stronach, a na pięciu z nich kafelek jest JEDYNYM filtrem
 * ekranu. `<div>` odebrałby je klawiaturze i czytnikowi ekranu — cztery zapadki pilnują tego
 * wprost, bo klikalny kafelek był świadomą decyzją dostępnościową. Kafelek, który tylko
 * pokazuje liczbę, jest `<div>`-em i zawsze nim był.
 *
 * @param {string}  badge        Etykieta nad tytułem, wzorzec „SEKCJA / Podsekcja".
 * @param {string}  badgeDotColor Kolor pulsującej kropki odznaki (domyślnie błękit wzorca).
 * @param {node}    avatar       Opcjonalny awatar/inicjały przy tytule (e-teczka: wybrana osoba).
 * @param {node}    title        Tytuł strony (renderowany jako h1).
 * @param {node}    subtitle     Zdanie opisujące, po co ta strona jest.
 * @param {Array}   stats        Kafelki KPI: {key,label,value,icon,color,title,onClick,active}.
 * @param {node}    actions      Akcje po prawej stronie nagłówka.
 * @param {node}    children     Dodatkowa treść w lewej kolumnie (rzadko potrzebna).
 * @param {number}  custom       Indeks opóźnienia animacji `fadeUp` (domyślnie 0).
 * @param {boolean} compact      Wariant zwarty dla ekranów roboczych (kolejka zgłoszeń):
 *                               ta sama tożsamość, mniej pionu — lista dostaje miejsce.
 * @param {"normalna"|"zwarta"} gestosc
 *        [zgłoszenie właściciela 2026-09-16] „/itam/przeglad — popraw mi UI (…) jest to zbyt duże,
 *        to nie musi być tak jebitne"; na pytanie o zakres wskazał CAŁY wstęp nad tabelą i wszystkie
 *        podstrony ITAM. `gestosc="zwarta"` to `compact` PLUS zwarte kafelki liczników: niższe,
 *        z mniejszą liczbą i ciaśniejszym odstępem. Nic nie znika — ośmiu liczników przeglądu
 *        nie da się zredukować, bo każdy odpowiada na inne pytanie; da się je zmieścić niżej.
 *
 *        ⚠️ DLACZEGO OSOBNY STOPIEŃ, A NIE ROZSZERZENIE `compact`: `compact` podaje dziś osiem
 *        stron panelu (dostęp, analityka, integracje, widoczność menu, stopki, czas pracy…).
 *        Dopisanie tam zwartych liczników zmieniłoby wygląd ekranów, o które nikt nie prosił.
 */
export default function NaglowekPodstrony({
    badge,
    badgeDotColor,
    avatar,
    title,
    subtitle,
    stats,
    actions,
    children,
    custom = 0,
    compact = false,
    gestosc = "normalna",
    className = "",
    ...reszta
}) {
    const kafelki = Array.isArray(stats) ? stats.filter(Boolean) : [];
    const zwarta = gestosc === "zwarta";
    const klasy = [
        styles.hero,
        compact || zwarta ? styles.compact : "",
        zwarta ? styles.licznikiZwarte : "",
        className,
    ]
        .filter(Boolean)
        .join(" ");
    const klasyLewej = [styles.heroLeft, avatar ? styles.heroLeftZAwatarem : ""]
        .filter(Boolean)
        .join(" ");

    return (
        <motion.div
            className={klasy}
            /* Znacznik dla zapadki „każda przepięta strona ma nagłówek" — w teście CSS-modules
               nie generuje hashowanych klas, więc po samej klasie nie da się go rozpoznać. */
            data-testid="page-hero"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={custom}
            {...reszta}
        >
            <div className={styles.heroGlow} />
            <div className={styles.heroPattern} />
            <div className={styles.heroContent}>
                <div className={klasyLewej}>
                    {badge && (
                        <span className={styles.heroBadge}>
                            <span
                                className={styles.heroBadgeDot}
                                style={badgeDotColor ? { background: badgeDotColor } : undefined}
                            />
                            {badge}
                        </span>
                    )}

                    {avatar && <span className={styles.heroAvatar}>{avatar}</span>}
                    {title && <h1 className={styles.heroTitle}>{title}</h1>}
                    {subtitle && <p className={styles.heroSubtitle}>{subtitle}</p>}

                    {children}
                </div>

                {(kafelki.length > 0 || actions) && (
                    <div className={styles.heroRight}>
                        {kafelki.length > 0 && (
                            <div className={styles.heroStats}>
                                {kafelki.map((k, i) => {
                                    const klikalny = typeof k.onClick === "function";
                                    const klasaKafelka = [
                                        styles.heroStat,
                                        klikalny ? styles.heroStatButton : "",
                                        k.active ? styles.heroStatActive : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ");
                                    const wnetrze = (
                                        <>
                                            {k.icon && (
                                                <span
                                                    className={styles.heroStatIcon}
                                                    style={k.color ? { color: k.color } : undefined}
                                                >
                                                    {k.icon}
                                                </span>
                                            )}
                                            <span className={styles.heroStatValue}>{k.value}</span>
                                            <span className={styles.heroStatLabel}>{k.label}</span>
                                        </>
                                    );
                                    // Kafelek, który coś robi, MUSI być przyciskiem — inaczej nie da się go
                                    // dosięgnąć klawiaturą ani czytnikiem ekranu.
                                    return klikalny ? (
                                        <button
                                            type="button"
                                            key={k.key || k.label || i}
                                            className={klasaKafelka}
                                            title={k.title || k.label}
                                            aria-pressed={k.active ? true : undefined}
                                            onClick={k.onClick}
                                        >
                                            {wnetrze}
                                        </button>
                                    ) : (
                                        <div
                                            key={k.key || k.label || i}
                                            className={klasaKafelka}
                                            title={k.title || undefined}
                                        >
                                            {wnetrze}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {actions && <div className={styles.heroActions}>{actions}</div>}
                    </div>
                )}
            </div>
        </motion.div>
    );
}
